import { AlertTriangle, CalendarX, Lock } from 'lucide-react';
import { ErrorState, Skeleton, StatusBadge } from '@/design/components';
import { cn } from '@/lib/cn';
import { toDateKey } from '@/lib/dates';
import { monthFill, useScheduleMonth, type MonthDay } from './monthApi';

const WEEKDAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

/**
 * חודש שלם של סניף אחד, כלוח שנה.
 *
 * מה שאי אפשר לראות בלוח שבועי: איפה נשארו חורים בעוד שבועיים,
 * איזה שבוע כבר פורסם ואיזה עדיין טיוטה, ומי מופיע חמישה ימים
 * ברצף. התשובות האלה הן על פני חודש, ולכן הן דורשות חודש על
 * המסך.
 *
 * לקריאה בלבד, ובכוונה: תא של יום בלוח חודשי הוא שלושים פיקסלים
 * גובה, וגרירה לתוכו היא הבטחה שלא תעמוד בה. לחיצה על יום פותחת
 * את השבוע שלו בלוח השבועי, ושם משבצים.
 */
export function MonthGrid({
  month,
  branchId,
  onPickDay,
}: {
  /** תאריך כלשהו בחודש המבוקש. */
  month: string;
  branchId: string;
  /** מעבר ללוח השבועי של היום שנלחץ. */
  onPickDay: (date: string) => void;
}) {
  const board = useScheduleMonth(month, branchId);
  const today = toDateKey(new Date());

  if (board.isPending) return <Skeleton className="h-96" />;
  if (board.isError) return <ErrorState onRetry={() => void board.refetch()} />;
  if (!board.data) return null;

  const data = board.data;
  const { filled, required } = monthFill(data);
  const percent = required === 0 ? 0 : Math.round((filled / required) * 100);
  const done = required > 0 && filled >= required;

  /** מצב הסידור של השבוע שהיום הזה נופל בו. */
  const statusOf = (date: string) => {
    const sunday = new Date(`${date}T12:00:00`);
    sunday.setDate(sunday.getDate() - sunday.getDay());
    const key = toDateKey(sunday);
    return data.periods.find((period) => period.start_date.slice(0, 10) === key)?.status;
  };

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="m-0 flex items-center gap-2 text-title font-semibold text-ink">
          <span
            aria-hidden="true"
            className="size-3 shrink-0 rounded-pill border border-line"
            style={data.branch.color ? { backgroundColor: data.branch.color } : undefined}
          />
          {data.branch.name}
        </h2>

        {required > 0 && (
          <span className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="block h-1.5 w-24 overflow-hidden rounded-pill bg-olive-soft"
            >
              <span
                className={cn('block h-full rounded-pill', done ? 'bg-good' : 'bg-olive')}
                style={{ width: `${percent}%` }}
              />
            </span>
            <span className={cn('text-label', done ? 'font-medium text-good' : 'text-muted')}>
              {done ? 'החודש מאויש במלואו' : `מאויש ${filled} מתוך ${required} בחודש`}
            </span>
          </span>
        )}

        <span className="grow" />
        <span className="text-label text-muted">לחיצה על יום פותחת את השבוע שלו</span>
      </div>

      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-sm border border-line bg-line">
        {WEEKDAYS.map((name) => (
          <div key={name} className="bg-cream p-1.5 text-center text-label text-muted">
            {name}
          </div>
        ))}

        {data.days.map((day) => (
          <DayCell
            key={day.date}
            day={day}
            isToday={day.date.slice(0, 10) === today}
            status={statusOf(day.date)}
            onPick={() => onPickDay(day.date.slice(0, 10))}
          />
        ))}
      </div>
    </div>
  );
}

function DayCell({
  day,
  isToday,
  status,
  onPick,
}: {
  day: MonthDay;
  isToday: boolean;
  status: string | undefined;
  onPick: () => void;
}) {
  const date = day.date.slice(0, 10);
  const dayNumber = Number(date.slice(8, 10));

  const open = day.shifts.filter((shift) => !shift.is_closed);
  const closed = day.shifts.find((shift) => shift.is_closed);
  const allClosed = open.length === 0 && closed !== undefined;

  const required = open.reduce((total, shift) => total + shift.required, 0);
  const filled = open.reduce(
    (total, shift) => total + Math.min(shift.assigned.length, shift.required),
    0,
  );
  const missing = Math.max(0, required - filled);

  return (
    <button
      type="button"
      onClick={onPick}
      aria-label={`${date}, ${
        allClosed ? 'סגור' : missing === 0 ? 'מאויש' : `חסרים ${missing}`
      }. פתיחת השבוע`}
      className={cn(
        'grid min-h-24 cursor-pointer content-start gap-1 p-1.5 text-start transition-colors',
        day.in_month ? 'bg-raised hover:bg-cream' : 'bg-cream/60 hover:bg-cream',
        isToday && 'ring-2 ring-inset ring-olive',
      )}
    >
      <span className="flex items-center gap-1">
        <span
          className={cn(
            'tabular text-body-sm',
            day.in_month ? 'font-medium text-ink' : 'text-muted',
          )}
        >
          {dayNumber}
        </span>

        {status === 'published' && (
          <Lock size={11} strokeWidth={2} aria-label="הסידור פורסם" className="text-muted" />
        )}

        <span className="grow" />

        {/* הצבע אינו ההודעה (כלל 10): המספר והאייקון הם */}
        {!allClosed && missing > 0 && day.in_month && (
          <span className="inline-flex items-center gap-0.5 text-label font-medium text-warn">
            <AlertTriangle size={10} strokeWidth={2.5} aria-hidden="true" />
            {missing}
          </span>
        )}
      </span>

      {allClosed ? (
        <span className="inline-flex items-center gap-1 text-label text-muted">
          <CalendarX size={11} strokeWidth={1.75} aria-hidden="true" />
          {closed?.closed_label ?? 'סגור'}
        </span>
      ) : (
        <span className="grid gap-0.5">
          {open.map((shift) => (
            <span key={shift.shift_type_id} className="grid gap-0.5">
              {shift.assigned.map((person) => (
                <span
                  key={person.id}
                  className="truncate rounded-sm bg-olive-soft px-1 text-label text-ink"
                  title={`${shift.name} · ${person.name}`}
                >
                  {person.name}
                </span>
              ))}
            </span>
          ))}

          {required > 0 && filled === required && (
            <StatusBadge tone="good" className="justify-self-start border-0 px-0">
              מאויש
            </StatusBadge>
          )}
        </span>
      )}
    </button>
  );
}
